import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { fmtError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to={location.state?.from?.pathname || "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full bg-background flex">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative w-1/2 bg-white text-foreground overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1599159876544-8bd9fbab6196?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img
            src="https://customer-assets.emergentagent.com/job_deal-discipline/artifacts/gq30gt0t_RENVIR%20%283%29.png"
            alt="RENVIRO SYSTEMS"
            className="h-44 xl:h-52 w-auto self-start select-none -ml-3"
            draggable="false"
          />
          <div className="space-y-6 max-w-md">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-primary-700">
              Discipline · Memory · Speed
            </p>
            <h1 className="font-display font-black text-4xl xl:text-5xl leading-[1.05] text-foreground">
              Never miss a follow-up. <br />
              <span className="text-primary">Capture every conversation.</span>
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed">
              A focused workspace for the RENVIRO sales team. Less form-filling. More closed deals.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">© RENVIRO SYSTEMS · Industrial reliability</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex flex-col items-start">
            <img
              src="https://customer-assets.emergentagent.com/job_deal-discipline/artifacts/gq30gt0t_RENVIR%20%283%29.png"
              alt="RENVIRO SYSTEMS"
              className="h-24 sm:h-28 w-auto select-none -ml-3"
              draggable="false"
            />
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mt-1">Sales Memory</p>
          </div>
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Sign in</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Welcome back.</h2>
          <p className="text-sm text-muted-foreground mt-2 mb-8">Use your approved company email.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@renvirosystems.com"
                className="h-12 rounded-2xl bg-muted/40 border-border"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-2xl bg-muted/40 border-border"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-2xl text-base font-semibold tap"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={onGoogle}
            className="w-full h-12 rounded-2xl tap"
            data-testid="login-google-button"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
              <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#fbbc04" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18a11 11 0 0 0 0 9.92l3.66-2.84z"/>
              <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-xs text-muted-foreground leading-relaxed">
            Access requires admin approval. Need access? Ask your team admin to add your email.
          </p>
        </div>
      </div>
    </div>
  );
}
